import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { MdCalendarMonth, MdInfoOutline } from 'react-icons/md';
import { toast } from 'react-toastify';
import DashboardLayout from './DashboardLayout';
import { ApiLoadState, ApiLoadTableRow } from './ApiLoadState';
import { useAuth } from '../../context/AuthContext';
import { useGetEmployeeByIdQuery } from '../../redux/employeeApi';
import {
  useCreateAccountBehaviourTrackingMutation,
  useGetAccountBehaviourTrackingsQuery,
  useGetBehaviourEmployeesQuery,
  useGetBehaviourMasterDataQuery,
  useUpdateAccountBehaviourTrackingMutation,
} from '../../redux/behaviourApi';
import { extractTrackingList, trackingAccountIdFromRow } from '../../utils/behaviourTracking';
import { BEHAVIOUR_REMARKS_MAX_LEN } from '../../utils/validation';
import { employeeListBackLabel, employeeListBackPath } from '../../utils/employeeListState';
import { behaviourListBackPath } from '../../utils/behaviourListState';

const COMPLIANCE_STATUS = 'compliance';
const NON_COMPLIANCE_STATUS = 'non_compliance';
const COMPLIANCE_RATING = 5;
const NON_COMPLIANCE_RATING = 0;
const COMPLIANCE_REMARKS = 'Compliance';

const RATING_OPTIONS = [
  { value: COMPLIANCE_STATUS, label: 'Compliance' },
  { value: NON_COMPLIANCE_STATUS, label: 'Non Compliance' },
];

const formInputClasses =
  'w-full rounded-lg border border-gray-200 bg-[#faf8f5] px-3 py-2 text-sm text-gray-900 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand';

function toISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function todayISODate() {
  return toISODate(new Date());
}

/** Earliest selectable tracking date: 7 days before today (inclusive week ending today). */
function trackingDateMinISO() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - 7);
  return toISODate(d);
}

function toDateInputValue(value) {
  if (!value) return '';
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  return toISODate(d);
}

function formatTrackingDateLabel(value) {
  const iso = toDateInputValue(value);
  if (!iso) return '—';
  const [year, month, day] = iso.split('-');
  if (!year || !month || !day) return '—';
  return `${day}/${month}/${year}`;
}

function isTrackingDateWithinLastWeek(value, existingDate = '') {
  const s = String(value || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const weekMin = trackingDateMinISO();
  const max = todayISODate();
  const existing = toDateInputValue(existingDate);
  const min = existing && existing < weekMin ? existing : weekMin;
  return s >= min && s <= max;
}

function extractParameters(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  return [];
}

function extractTrackingRecord(payload) {
  if (!payload || typeof payload !== 'object') return null;
  if (payload.data && typeof payload.data === 'object' && !Array.isArray(payload.data)) {
    return payload.data;
  }
  if (payload.id != null || payload.tracking_date != null || Array.isArray(payload.behaviours)) {
    return payload;
  }
  return null;
}

function extractBehavioursFromTracking(record) {
  if (!record || typeof record !== 'object') return [];
  if (Array.isArray(record.behaviours)) return record.behaviours;
  if (Array.isArray(record.behaviour_details)) return record.behaviour_details;
  if (Array.isArray(record.account_behaviour_trackings)) return record.account_behaviour_trackings;
  if (Array.isArray(record.details)) return record.details;
  return [];
}

function emptyRowState() {
  return {
    checked: true,
    rating: COMPLIANCE_STATUS,
    occurrences: '',
    remarks: '',
  };
}

/** true = compliance, false = non_compliance, null = unknown */
function parseComplianceStatus(value) {
  const s = String(value ?? '').trim().toLowerCase();
  if (!s) return null;
  if (s === COMPLIANCE_STATUS || s === 'compliant' || s === String(COMPLIANCE_RATING)) {
    return true;
  }
  if (
    s === NON_COMPLIANCE_STATUS ||
    s === 'non-compliance' ||
    s === 'noncompliance' ||
    s === String(NON_COMPLIANCE_RATING)
  ) {
    return false;
  }
  return null;
}

function isRowCompliance(row) {
  if (!row) return true;
  const fromRating = parseComplianceStatus(row.rating);
  if (fromRating != null) return fromRating;
  return row.checked !== false;
}

function rowRatingDisplay(row) {
  return isRowCompliance(row) ? COMPLIANCE_STATUS : NON_COMPLIANCE_STATUS;
}

function getNonComplianceCount(item) {
  if (!item || typeof item !== 'object') return null;
  const value = item.non_compliance_count ?? item.nonComplianceCount ?? item.occurrences;
  if (value == null || value === '') return null;
  return value;
}

function isComplianceBehaviourItem(item) {
  if (!item || typeof item !== 'object') return true;
  const status = String(
    item.compliance_status ?? item.complianceStatus ?? ''
  )
    .trim()
    .toLowerCase();
  if (status === COMPLIANCE_STATUS || status === 'compliant') return true;
  if (
    status === NON_COMPLIANCE_STATUS ||
    status === 'non-compliance' ||
    status === 'noncompliance'
  ) {
    return false;
  }
  if (item.non_compliance === true || item.nonCompliance === true) return false;
  if (item.compliance === false || item.is_compliance === false) return false;
  if (item.non_compliance === false || item.nonCompliance === false) return true;
  if (item.compliance === true || item.is_compliance === true) return true;

  const ratingRaw = item.rating;
  if (ratingRaw !== '' && ratingRaw != null && Number(ratingRaw) === NON_COMPLIANCE_RATING) {
    return false;
  }
  return true;
}

function rowStateFromBehaviourItem(item) {
  const countRaw = getNonComplianceCount(item);
  const occurrences = countRaw == null ? '0' : String(countRaw);
  if (isComplianceBehaviourItem(item)) {
    return {
      ...emptyRowState(),
      occurrences,
    };
  }
  return {
    checked: false,
    rating: NON_COMPLIANCE_STATUS,
    occurrences,
    remarks: String(item?.manager_remarks ?? item?.remarks ?? item?.remark ?? '').trim(),
  };
}

function ParameterInfoIcon({ description }) {
  if (!description) return null;
  return (
    <span className="group relative inline-flex shrink-0">
      <MdInfoOutline
        className="h-4 w-4 cursor-help text-gray-400 hover:text-brand"
        aria-label={description}
      />
      <span
        role="tooltip"
        className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 hidden w-56 -translate-x-1/2 rounded-lg bg-gray-900 px-3 py-2 text-xs font-normal leading-relaxed text-white shadow-lg group-hover:block"
      >
        {description}
        <span className="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
      </span>
    </span>
  );
}

function AdminEmployeeBehaviourTracking() {
  const { id, trackingId } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, area, canBehaviourTrack, canAction } = useAuth();
  const isAdmin = area === 'admin';
  const homePath = isAdmin ? '/dashboard/admin' : '/dashboard/employee';
  const isEditMode = Boolean(trackingId);
  const canEditByRole = canAction('behaviour_tracking', 'edit');
  const canCreateByRole = canAction('behaviour_tracking', 'create');
  const canAccessForm = isEditMode
    ? canEditByRole || canBehaviourTrack
    : canBehaviourTrack || canCreateByRole;

  const trackingFromNav =
    location.state?.tracking && typeof location.state.tracking === 'object'
      ? location.state.tracking
      : null;

  const accountIdFromQuery = String(searchParams.get('account_id') || '').trim();
  const accountIdFromNav = String(
    location.state?.accountId ??
      trackingFromNav?.account_id ??
      trackingFromNav?.accountId ??
      ''
  ).trim();
  const seedAccountId = accountIdFromQuery || accountIdFromNav;

  /** Show-by-id API returns 422; load from list by account_id (+ nav state), else scan list. */
  const {
    data: trackingsListPayload,
    isLoading: trackingLoading,
    isFetching: trackingFetching,
    error: trackingError,
    refetch: refetchTracking,
  } = useGetAccountBehaviourTrackingsQuery(
    {
      accountId: seedAccountId || undefined,
      per_page: 500,
      page: 1,
    },
    {
      skip: !isEditMode || Boolean(trackingFromNav),
    }
  );

  const trackingRecord = useMemo(() => {
    if (trackingFromNav) {
      const navId = trackingFromNav.id ?? trackingFromNav.tracking_id;
      if (navId == null || String(navId) === String(trackingId)) {
        return extractTrackingRecord(trackingFromNav) || trackingFromNav;
      }
    }
    const list = extractTrackingList(trackingsListPayload);
    const match = list.find(
      (row) => String(row?.id ?? row?.tracking_id) === String(trackingId)
    );
    return match ? extractTrackingRecord(match) || match : null;
  }, [trackingFromNav, trackingsListPayload, trackingId]);

  const resolveNumericAccountId = (...candidates) => {
    for (const value of candidates) {
      if (value == null || value === '') continue;
      const n = Number(value);
      if (Number.isFinite(n) && n > 0) return n;
    }
    return null;
  };

  const employeeId = isEditMode
    ? String(
        trackingAccountIdFromRow(trackingRecord) ??
          trackingAccountIdFromRow(trackingFromNav) ??
          resolveNumericAccountId(seedAccountId, location.state?.accountId) ??
          ''
      )
    : id;
  const accountId = isEditMode
    ? resolveNumericAccountId(employeeId, seedAccountId)
    : resolveNumericAccountId(employeeId, seedAccountId, id);
  const existingTrackingDate = toDateInputValue(
    trackingRecord?.tracking_date ?? trackingRecord?.date ?? ''
  );

  const trackingNotFound =
    isEditMode &&
    !trackingFromNav &&
    !trackingLoading &&
    !trackingFetching &&
    !trackingRecord;

  const {
    data: employeePayload,
    isLoading: employeeLoading,
    isFetching: employeeFetching,
    error: employeeError,
    refetch: refetchEmployee,
  } = useGetEmployeeByIdQuery(employeeId, {
    skip: employeeId == null || employeeId === '',
  });

  const {
    data: masterPayload,
    isLoading: masterLoading,
    isFetching: masterFetching,
    error: masterError,
    refetch: refetchMaster,
  } = useGetBehaviourMasterDataQuery();

  const { data: behaviourEmployeesRes } = useGetBehaviourEmployeesQuery(undefined, {
    skip: true,
  });

  const accountIdFromEmployees = useMemo(() => {
    if (!isEditMode) return null;
    const list =
      behaviourEmployeesRes?.data ??
      behaviourEmployeesRes?.employees ??
      behaviourEmployeesRes ??
      [];
    if (!Array.isArray(list) || !list.length) return null;

    const code = String(
      location.state?.employeeCode ||
        trackingRecord?.employee_code ||
        trackingRecord?.employee_id ||
        trackingRecord?.emp_id ||
        trackingFromNav?.employee_code ||
        trackingFromNav?.employee_id ||
        ''
    ).trim();
    const name = String(
      trackingRecord?.employee_name ||
        trackingRecord?.account_name ||
        trackingFromNav?.employee_name ||
        ''
    )
      .trim()
      .toLowerCase();

    const match =
      list.find((emp) => {
        const empCode = String(emp?.employee_code || emp?.emp_id || '').trim();
        const empAccount = String(emp?.employee_id ?? emp?.account_id ?? emp?.id ?? '').trim();
        if (code && (empCode === code || empAccount === code)) return true;
        return false;
      }) ||
      (name
        ? list.find((emp) => String(emp?.name || '').trim().toLowerCase() === name)
        : null);

    if (!match) return null;
    const id = match.employee_id ?? match.account_id ?? match.id;
    const n = Number(id);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [
    isEditMode,
    behaviourEmployeesRes,
    location.state?.employeeCode,
    trackingRecord,
    trackingFromNav,
  ]);

  const [createTracking, { isLoading: isCreating }] =
    useCreateAccountBehaviourTrackingMutation();
  const [updateTracking, { isLoading: isUpdating }] =
    useUpdateAccountBehaviourTrackingMutation();
  const isSaving = isCreating || isUpdating;

  const employee =
    employeePayload?.data &&
    typeof employeePayload.data === 'object' &&
    !Array.isArray(employeePayload.data)
      ? employeePayload.data
      : employeePayload?.data?.[0] ?? employeePayload ?? null;

  const parameters = useMemo(() => extractParameters(masterPayload), [masterPayload]);

  const [trackingDate, setTrackingDate] = useState(todayISODate);
  const [rows, setRows] = useState({});
  const [formHydrated, setFormHydrated] = useState(!isEditMode);
  const trackingDateMax = useMemo(() => todayISODate(), []);
  const trackingDateMin = useMemo(() => {
    const weekMin = trackingDateMinISO();
    if (isEditMode && existingTrackingDate && existingTrackingDate < weekMin) {
      return existingTrackingDate;
    }
    return weekMin;
  }, [isEditMode, existingTrackingDate]);

  useEffect(() => {
    if (isEditMode) {
      setRows((prev) => {
        const next = { ...prev };
        parameters.forEach((param) => {
          const key = String(param.id ?? param.code);
          if (!next[key]) next[key] = emptyRowState();
        });
        return next;
      });
      return;
    }
    const next = {};
    parameters.forEach((param) => {
      const key = String(param.id ?? param.code);
      next[key] = emptyRowState();
    });
    setRows(next);
  }, [parameters, isEditMode]);

  useEffect(() => {
    if (!isEditMode) {
      setFormHydrated(true);
      return;
    }
    if (!trackingRecord) return;
    const dateValue = toDateInputValue(
      trackingRecord.tracking_date ?? trackingRecord.date ?? ''
    );
    if (dateValue) setTrackingDate(dateValue);

    const behaviourItems = extractBehavioursFromTracking(trackingRecord);
    if (!behaviourItems.length && !parameters.length) return;

    setRows((prev) => {
      const next = { ...prev };
      parameters.forEach((param) => {
        const key = String(param.id ?? param.code);
        if (!next[key]) next[key] = emptyRowState();
      });
      behaviourItems.forEach((item) => {
        const key = String(item.behaviour_id ?? item.id ?? item.code ?? '');
        if (!key) return;
        next[key] = rowStateFromBehaviourItem(item);
      });
      return next;
    });
    setFormHydrated(true);
  }, [isEditMode, trackingRecord, parameters]);

  const updateRow = (key, field, value) => {
    setRows((prev) => {
      const current = prev[key] || emptyRowState();
      const nextValue =
        field === 'remarks'
          ? String(value).slice(0, BEHAVIOUR_REMARKS_MAX_LEN)
          : value;
      const next = { ...current, [field]: nextValue };
      if (field === 'checked' && value === true) {
        next.rating = COMPLIANCE_STATUS;
      }
      if (field === 'checked' && value === false) {
        next.rating = NON_COMPLIANCE_STATUS;
      }
      if (field === 'rating' && parseComplianceStatus(value) === true) {
        next.rating = COMPLIANCE_STATUS;
        next.checked = true;
      }
      if (field === 'rating' && parseComplianceStatus(value) === false) {
        next.rating = NON_COMPLIANCE_STATUS;
        next.checked = false;
      }
      return { ...prev, [key]: next };
    });
  };

  const buildBehavioursPayload = (rowState) =>
    parameters.map((param) => {
      const key = String(param.id ?? param.code);
      const row = rowState[key] || emptyRowState();
      const complianceStatus = rowRatingDisplay(row);

      return {
        behaviour_id: Number(param.id),
        behaviour_name: param.code || param.parameter || '',
        compliance_status: complianceStatus,
        manager_remarks:
          complianceStatus === COMPLIANCE_STATUS
            ? String(row.remarks || '').trim().slice(0, BEHAVIOUR_REMARKS_MAX_LEN) ||
              COMPLIANCE_REMARKS
            : String(row.remarks || '')
                .trim()
                .slice(0, BEHAVIOUR_REMARKS_MAX_LEN),
      };
    });

  const isPending =
    employeeLoading ||
    employeeFetching ||
    masterLoading ||
    masterFetching ||
    (isEditMode &&
      !trackingRecord &&
      !trackingFromNav &&
      (trackingLoading || trackingFetching || !formHydrated));
  const error =
    employeeError ||
    masterError ||
    (trackingError && !trackingFromNav ? trackingError : null);
  const refetch = () => {
    refetchEmployee();
    refetchMaster();
    if (isEditMode && !trackingFromNav) refetchTracking();
  };

  const employeeName =
    employee?.name ||
    employee?.employee_name ||
    employee?.account_name ||
    trackingRecord?.employee_name ||
    trackingRecord?.account_name ||
    'Employee';
  const employeeCode =
    employee?.employee_id ||
    employee?.emp_id ||
    trackingRecord?.employee_id ||
    trackingRecord?.employee_code ||
    '';
  const showEmpty = !error && !isPending && parameters.length === 0;
  const showData = !error && parameters.length > 0;
  const backPath = isEditMode
    ? behaviourListBackPath(location)
    : employeeListBackPath(location);
  const listCrumbLabel = isEditMode
    ? 'Behaviour Tracking'
    : employeeListBackLabel(location);
  const pageCrumbLabel = isEditMode ? 'Edit' : 'Behaviour Tracking';
  const breadcrumbs = (
    <div className="flex items-center justify-end">
      <p className="text-sm text-gray-500">
        <Link to={homePath} className="text-brand hover:underline">
          Home
        </Link>
        {' / '}
        <Link to={backPath} className="text-brand hover:underline">
          {listCrumbLabel}
        </Link>
        {' / '}
        <span className="text-gray-400">{pageCrumbLabel}</span>
      </p>
    </div>
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    const resolvedAccountId = resolveNumericAccountId(
      accountId,
      accountIdFromEmployees,
      seedAccountId,
      employeeId,
      employee?.id,
      trackingRecord?.account_id,
      trackingRecord?.accountId,
      trackingFromNav?.account_id,
      location.state?.accountId,
      searchParams.get('account_id'),
      !isEditMode ? id : null
    );
    if (!isEditMode && !resolvedAccountId) {
      toast.error('Invalid employee.');
      return;
    }
    if (!isEditMode) {
      if (!trackingDate) {
        toast.error('Please select tracking date.');
        return;
      }
      if (!isTrackingDateWithinLastWeek(trackingDate, existingTrackingDate)) {
        toast.error('Tracking date must be within the last 1 week (including today).');
        return;
      }
    }
    const reviewedBy = Number(user?.id);
    if (!Number.isFinite(reviewedBy) || reviewedBy <= 0) {
      toast.error('Unable to identify reviewer. Please login again.');
      return;
    }
    if (!parameters.length) {
      toast.error('No behaviour parameters found.');
      return;
    }

    const uncheckedParams = parameters.filter((param) => {
      const key = String(param.id ?? param.code);
      const row = rows[key] || emptyRowState();
      return !isRowCompliance(row);
    });

    for (const param of uncheckedParams) {
      const key = String(param.id ?? param.code);
      const row = rows[key] || emptyRowState();
      const label = param.code || param.parameter || key;
      if (row.rating === '' || row.rating == null) {
        toast.error(`Please select rating for ${label}.`);
        return;
      }
      if (row.occurrences === '' || row.occurrences == null) {
        toast.error(`Please enter number of occurrences for ${label}.`);
        return;
      }
      const remarks = String(row.remarks || '').trim();
      if (!remarks) {
        toast.error(`Please enter manager remarks for ${label}.`);
        return;
      }
      if (remarks.length > BEHAVIOUR_REMARKS_MAX_LEN) {
        toast.error(
          `Remarks for ${label} must be ${BEHAVIOUR_REMARKS_MAX_LEN} characters or less.`
        );
        return;
      }
    }

    const behaviours = buildBehavioursPayload(rows);

    const payload = {
      tracking_date: trackingDate,
      reviewed_by: reviewedBy,
      behaviours,
    };
    if (resolvedAccountId) payload.account_id = resolvedAccountId;

    try {
      if (isEditMode) {
        await updateTracking({ id: trackingId, ...payload }).unwrap();
        toast.success('Behaviour tracking updated successfully.');
        navigate(backPath);
      } else {
        await createTracking(payload).unwrap();
        toast.success('Behaviour tracking saved successfully.');
        navigate(backPath);
      }
    } catch (err) {
      toast.error(
        err?.data?.message ||
          (isEditMode
            ? 'Failed to update behaviour tracking.'
            : 'Failed to save behaviour tracking.')
      );
    }
  };

  if (!canAccessForm) {
    return (
      <Navigate
        to={isEditMode ? behaviourListBackPath(location) : employeeListBackPath(location)}
        replace
      />
    );
  }

  if (trackingNotFound) {
    return (
      <DashboardLayout title="Edit Behaviour Tracking">
        <div className="space-y-4 py-4">
          {breadcrumbs}
          <div className="rounded-2xl bg-white p-6 text-center shadow-sm">
            <p className="text-sm text-gray-600">
              Behaviour tracking record not found. Open it again from the Behaviour Tracking list.
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title={isEditMode ? 'Edit Behaviour Tracking' : 'Behaviour Tracking'}>
      <div className="space-y-4 py-4">
        {breadcrumbs}
        <form onSubmit={handleSubmit}>
        <div className="rounded-2xl bg-white p-4 shadow-sm sm:p-6">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="min-w-0 text-base font-semibold text-gray-900">
              {employeeName}
              {employeeCode ? (
                <span className="ml-2 text-sm font-normal text-gray-500">
                  ({employeeCode})
                </span>
              ) : null}
            </h2>
            {isEditMode ? (
              <span className="inline-flex shrink-0 items-center gap-2 rounded-full border border-gray-200 bg-[#faf8f5] px-3 py-1.5 text-sm">
                <MdCalendarMonth className="h-4 w-4 text-brand" aria-hidden="true" />
                <span className="text-gray-500">Tracking Date</span>
                <span className="font-semibold text-gray-900">
                  {formatTrackingDateLabel(trackingDate || existingTrackingDate)}
                </span>
              </span>
            ) : null}
          </div>
          {!isEditMode ? (
            <div className="mb-4 max-w-xs">
              <label className="text-xs font-medium text-gray-600" htmlFor="tracking_date">
                Tracking Date
              </label>
              <input
                id="tracking_date"
                type="date"
                value={trackingDate}
                min={trackingDateMin}
                max={trackingDateMax}
                onChange={(e) => setTrackingDate(e.target.value)}
                className={`mt-1 ${formInputClasses}`}
                required
              />
            </div>
          ) : null}

          {showEmpty ? (
            <p className="py-8 text-center text-sm text-gray-500">No behaviour data found.</p>
          ) : null}

          <ApiLoadState
            isLoading={isPending && !parameters.length}
            isFetching={false}
            hasData={parameters.length > 0}
            error={error}
            refetch={refetch}
            message="Loading behaviour data…"
            className="py-6 md:hidden"
          />

          {showData ? (
            <ul className="mb-4 space-y-3 md:hidden">
              {parameters.map((param) => {
                const key = String(param.id ?? param.code);
                const row = rows[key] || emptyRowState();
                const paramName = param.parameter || param.name || '—';
                const isChecked = isRowCompliance(row);
                return (
                  <li
                    key={key}
                    className="space-y-3 rounded-lg border border-gray-100 bg-[#faf8f5] p-3"
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) => updateRow(key, 'checked', e.target.checked)}
                        disabled={isSaving}
                        className="mt-1 h-4 w-4 rounded border-gray-300 text-brand focus:ring-brand disabled:cursor-not-allowed"
                        aria-label={`Compliance for ${paramName}`}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-gray-500">
                          Code:{' '}
                          <span className="font-medium text-gray-800">{param.code || '—'}</span>
                        </p>
                        <p className="mt-1 flex items-center gap-1.5 text-sm font-medium text-gray-900">
                          {paramName}
                          <ParameterInfoIcon description={param.description} />
                        </p>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Rating (0–5)</label>
                      <select
                        value={rowRatingDisplay(row)}
                        onChange={(e) => updateRow(key, 'rating', e.target.value)}
                        className={`mt-1 ${formInputClasses}`}
                      >
                        {RATING_OPTIONS.map((item) => (
                          <option key={item.value} value={item.value}>
                            {item.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">No. of Occurrences</label>
                      <input
                        type="number"
                        min="0"
                        value={row.occurrences}
                        onChange={(e) => updateRow(key, 'occurrences', e.target.value)}
                        disabled={isEditMode || isChecked}
                        className={`mt-1 ${formInputClasses} disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400`}
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Manager Remarks</label>
                      <textarea
                        rows={2}
                        maxLength={BEHAVIOUR_REMARKS_MAX_LEN}
                        value={row.remarks}
                        onChange={(e) => updateRow(key, 'remarks', e.target.value)}
                        disabled={isChecked}
                        className={`mt-1 ${formInputClasses} disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400`}
                        placeholder="Remarks"
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : null}

          {!showEmpty ? (
            <div className="hidden overflow-x-auto md:block">
              <table className="min-w-full border-collapse text-sm">
                <thead>
                  <tr>
                    <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                      Code
                    </th>
                    <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                      Parameter
                    </th>
                    <th className="border-b border-gray-200 px-3 py-2.5 text-center font-semibold text-gray-700" />
                    <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                      Rating
                    </th>
                    <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                      No. of Occurrences
                    </th>
                    <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                      Manager Remarks
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <ApiLoadTableRow
                    colSpan={6}
                    isLoading={isPending && !parameters.length}
                    isFetching={isPending && parameters.length > 0}
                    hasData={parameters.length > 0}
                    error={error}
                    refetch={refetch}
                    message="Loading behaviour data..."
                  />
                  {showData
                    ? parameters.map((param) => {
                        const key = String(param.id ?? param.code);
                        const row = rows[key] || emptyRowState();
                        const paramName = param.parameter || param.name || '—';
                        const isChecked = isRowCompliance(row);
                        return (
                          <tr key={key} className="hover:bg-amber-50/40">
                            <td className="border-b border-gray-100 px-3 py-2.5 font-medium text-gray-700">
                              {param.code || '—'}
                            </td>
                            <td className="border-b border-gray-100 px-3 py-2.5 text-gray-700">
                              <span className="inline-flex items-center gap-1.5">
                                {paramName}
                                <ParameterInfoIcon description={param.description} />
                              </span>
                            </td>
                            <td className="border-b border-gray-100 px-3 py-2.5 text-center">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={(e) =>
                                  updateRow(key, 'checked', e.target.checked)
                                }
                                disabled={isSaving}
                                className="h-4 w-4 rounded border-gray-300 text-brand focus:ring-brand disabled:cursor-not-allowed"
                                aria-label={`Compliance for ${paramName}`}
                              />
                            </td>
                            <td className="border-b border-gray-100 px-3 py-2.5">
                              <select
                                value={rowRatingDisplay(row)}
                                onChange={(e) => updateRow(key, 'rating', e.target.value)}
                                className={formInputClasses}
                                aria-label={`Rating for ${paramName}`}
                              >
                                {RATING_OPTIONS.map((item) => (
                                  <option key={item.value} value={item.value}>
                                    {item.label}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td className="border-b border-gray-100 px-3 py-2.5">
                              <input
                                type="number"
                                min="0"
                                value={row.occurrences}
                                onChange={(e) =>
                                  updateRow(key, 'occurrences', e.target.value)
                                }
                                disabled={isEditMode || isChecked}
                                className={`${formInputClasses} disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400`}
                                placeholder="0"
                                aria-label={`Occurrences for ${paramName}`}
                              />
                            </td>
                            <td className="border-b border-gray-100 px-3 py-2.5">
                              <textarea
                                rows={2}
                                maxLength={BEHAVIOUR_REMARKS_MAX_LEN}
                                value={row.remarks}
                                onChange={(e) => updateRow(key, 'remarks', e.target.value)}
                                disabled={isChecked}
                                className={`${formInputClasses} disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400`}
                                placeholder="Remarks"
                                aria-label={`Remarks for ${paramName}`}
                              />
                            </td>
                          </tr>
                        );
                      })
                    : null}
                </tbody>
              </table>
            </div>
          ) : null}

          {showData ? (
            <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
              <Link
                to={backPath}
                className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </Link>
              <button
                type="submit"
                disabled={isSaving}
                className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSaving ? 'Saving…' : isEditMode ? 'Update' : 'Save'}
              </button>
            </div>
          ) : null}
        </div>
        </form>
      </div>
    </DashboardLayout>
  );
}

export default AdminEmployeeBehaviourTracking;
